import React, {
  PropsWithChildren,
  createContext,
  useEffect,
  useState,
} from "react";

import { z } from "zod";

import { SessionStorageCell } from "@/lib/browser-storage";

type SecondaryLayoutTopBannerContextType = {
  closeBanner: () => void;
  isBannerHidden: boolean;
};

const SecondaryLayoutTopBannerContext =
  createContext<SecondaryLayoutTopBannerContextType | null>(null);

const isBannerHiddenSessionState = new SessionStorageCell(
  z.boolean(),
  "secondary-layout-top-banner-is-hidden",
);

export const useSecondaryLayoutTopBannerContext = () => {
  const context = React.useContext(SecondaryLayoutTopBannerContext);

  if (!context) {
    throw new Error(
      "useSecondaryLayoutTopBanner must be used within a SecondaryLayoutTopBannerContextProvider",
    );
  }

  return context;
};

export const SecondaryLayoutTopBannerContextProvider: React.FC<
  PropsWithChildren
> = ({ children }) => {
  const [isBannerHidden, hideBanner] = useState(true);

  useEffect(() => {
    hideBanner(isBannerHiddenSessionState.get() ?? false);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      isBannerHiddenSessionState.set(false);
    };

    window.onbeforeunload = handleVisibilityChange;
  });

  const closeBanner = () => {
    hideBanner(true);
    isBannerHiddenSessionState.set(true);
  };

  return (
    <SecondaryLayoutTopBannerContext.Provider
      value={{ closeBanner, isBannerHidden }}
    >
      {children}
    </SecondaryLayoutTopBannerContext.Provider>
  );
};
